import {
  EntityObject,
  getRequiredObjectId,
  getRequiredObjectType,
  ObjectDocumentAssocUpload,
  ObjectPictureAssocUpload,
  useCreateObjectDocumentAssocMutation,
  useCreateObjectPictureAssocMutation
} from "@ots-frontend/ots-data";
import {useCallback} from "react";
import {useDisclosure} from "@mantine/hooks";
import {notifications} from "@mantine/notifications";
import {useTranslation} from "react-i18next";
import {useAuth} from "../../../providers/AuthProvider/AuthProvider";
import {SupplementalFormValues} from "../../form/SupplementalForm/SupplementalForm";
import { ResultTypeFrom } from "@reduxjs/toolkit/query";

export type UseEntitySupplementalUploadModalProps = {
  entity: EntityObject
}

export type UseEntitySupplementalUploadModalResult = {
  readonly opened: boolean
  readonly handlers: {
    readonly open: () => void;
    readonly close: () => void;
    readonly toggle: () => void;
  }
  readonly onSubmit: (values: SupplementalFormValues) => void;
  readonly isCreating: boolean
}

export const useEntitySupplementalUploadModal = (props: UseEntitySupplementalUploadModalProps): UseEntitySupplementalUploadModalResult => {
  const {entity} = props;

  const {userId} = useAuth()

  const [_opened, _handlers] = useDisclosure(false);

  const [_createDoc, {isLoading: _isCreatingDoc}] = useCreateObjectDocumentAssocMutation();
  const [_createPic, {isLoading: _isCreatingPic}] = useCreateObjectPictureAssocMutation();

  const {t} = useTranslation()

  const handleResult = useCallback((promise: Promise<ResultTypeFrom<any>>) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("commons.entity.message.alert.create.title", "Supplemental Created"),
          message: ""
        });
        _handlers.close()
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("commons.error", "Error"),
          message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"})
        })
      );
  }, [_handlers, t])

  const _onSubmit = useCallback((values: SupplementalFormValues) => {
    const fileExt = values.file.name.split(".").pop();

    if (fileExt) {
      if (fileExt === "png" || fileExt === "jpg" || fileExt === "jpeg") {
        const upload: ObjectPictureAssocUpload = {
          file: values.file,
          objectId: getRequiredObjectId(entity),
          objectType: getRequiredObjectType(entity),
          supplementalType: values.type?.code,
          fileName: values.displayName,
          description: values.displayName,
          reportable: values.reportable,
          personId: userId,
        }

        const promise = _createPic(upload).unwrap()
        handleResult(promise)
      } else {
        const upload: ObjectDocumentAssocUpload = {
          file: values.file,
          objectId: getRequiredObjectId(entity),
          objectType: getRequiredObjectType(entity),
          supplementalType: values.type?.code,
          fileName: values.displayName,
          description: values.displayName,
          reportable: values.reportable,
          personId: userId,
        }

        const promise = _createDoc(upload).unwrap()
        handleResult(promise)
      }
    } else {
      _handlers.close()
    }
  }, [_createPic, _createDoc, entity, userId, _handlers, handleResult])


  return {
    opened: _opened,
    handlers: _handlers,
    onSubmit: _onSubmit,
    isCreating: _isCreatingDoc || _isCreatingPic,
  };
};
